import React from 'react';

import { MainCard, PermissionsButtonWrapper, Text } from '~/components/core';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import { HandshakeIcon, PencilIcon } from '~/components/icons';
import { getDateFromServerDateTime } from '~/DateTimeUtils';

import CapacityAgreementSlots from './CapacityAgreementSlots';
import { TEXTS } from './texts';
import type { CapacityAgreementDisplayFieldsType, CapacityAgreementType, CapacitySlotType } from './types';

type DisplayFieldFuncType = (value: string | number | null | undefined) => string | number;

const displayField: DisplayFieldFuncType = (value) => (typeof value === 'number' ? value : value || TEXTS.missingField);
const displayDateField: DisplayFieldFuncType = (value) =>
  typeof value === 'string' ? getDateFromServerDateTime(value) : (value ?? TEXTS.missingField);

export const CAPACITY_AGREEMENT_DISPLAY_FIELDS = [
  { key: 'agreement_number', display: displayField },
  { key: 'umr', display: displayField },
  { key: 'lloyds_risk_code', display: displayField },
  { key: 'effective_date', display: displayDateField },
  { key: 'expiration_date', display: displayDateField },
] as const satisfies { key: CapacityAgreementDisplayFieldsType; display: DisplayFieldFuncType }[];

function CapacityAgreementCard({
  agreement,
  onEditClick,
  onEditSlotClick,
}: {
  agreement: CapacityAgreementType;
  onEditClick: () => void;
  onEditSlotClick: (capacitySlot: CapacitySlotType) => void;
}) {
  return (
    <MainCard type="contained" className="border-slate-500 p-16">
      <div className="flex items-center gap-12 text-slate-900">
        <HandshakeIcon iconColor="currentColor" size={20} />
        <Text variant={Text.VARIANTS.XL} weight={Text.WEIGHTS.SEMI_BOLD} className="mr-auto">
          {agreement.carrier_name}
        </Text>
        <PermissionsButtonWrapper>
          <IconButton onClick={onEditClick}>
            <PencilIcon />
            <span className="sr-only">{TEXTS.edit}</span>
          </IconButton>
        </PermissionsButtonWrapper>
      </div>
      <div className="flex flex-wrap gap-x-20 gap-y-32 rounded-md bg-slate-200 p-16">
        {CAPACITY_AGREEMENT_DISPLAY_FIELDS.map(({ key, display }) => (
          <div key={key} className="grid gap-4">
            <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.SEMI_BOLD} className="text-slate-700">
              {TEXTS.capacityFieldLabels[key]()}
            </Text>
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="text-slate-900">
              {display(agreement[key])}
            </Text>
          </div>
        ))}
      </div>
      <CapacityAgreementSlots slots={agreement.capacity_slots} onEditSlotClick={onEditSlotClick} />
    </MainCard>
  );
}

export default CapacityAgreementCard;
